import { useEffect, useMemo, useRef, useState } from 'react';
import { createProjectionObserver } from '../services/projectionObserver.service.js';
import { browserDeliveryClock } from '../clients/browserClock.client.js';
import { createProjectionLongTaskMonitor } from '../clients/projectionLongTask.client.js';
import { requestProjectionReceipt } from '../clients/projectionSocket.client.js';
import { PROJECTION_ACK_SCHEMA, PROJECTION_REVISION_KIND, projectionNegotiationState } from '../contracts/projectionObserver.contract.js';
import { PROJECTION_OBSERVER_POLICY } from '../policies/projectionObserver.policy.js';

export function useProjectionObserver({ enabled, ready, getOwner, getSequence, getRows, build = null }) {
  const ports = useRef({ getOwner, getSequence, getRows });
  ports.current = { getOwner, getSequence, getRows };
  const holder = useRef(null);
  if (!holder.current) holder.current = createProjectionObserver({ enabled, build, clock: browserDeliveryClock,
    longTasks: createProjectionLongTaskMonitor({ now: browserDeliveryClock.now }),
    getOwner: () => ports.current.getOwner(), getSequence: () => ports.current.getSequence(), getRows: () => ports.current.getRows(),
    send: (owner, payload, reply) => requestProjectionReceipt(owner.socket, 'ack:sportsnew:patch', payload, reply),
  });
  const service = holder.current;
  const [negotiation, setNegotiation] = useState('waiting');
  const sequence = getSequence();
  const sequenceKnown = Number.isSafeInteger(sequence) && sequence >= 0;
  const { socket, session, generation, mode, scope, filter, room } = getOwner();
  const owner = useMemo(() => ({ socket, session, generation, mode, scope, filter, room }),
    [socket, session, generation, mode, scope, filter, room]);
  useEffect(() => { service.sync(); });
  useEffect(() => {
    if (!enabled || !ready || !owner.socket?.connected || !owner.room || owner.mode !== 'live') {
      setNegotiation('waiting'); return undefined;
    }
    // A subscribed cold room has no ready-cut until its first real delta.
    // Only this unknown→known transition restarts negotiation, never each revision.
    if (!sequenceKnown) { setNegotiation('waiting_sequence'); return undefined; }
    let cancelled = false, timer = null, failures = 0;
    setNegotiation('negotiating');
    const negotiate = () => {
      if (cancelled) return;
      const startedAt = browserDeliveryClock.now();
      requestProjectionReceipt(owner.socket, 'capability:sportsnew:projection', {
        schema: PROJECTION_ACK_SCHEMA, revisionKind: PROJECTION_REVISION_KIND, room: owner.room,
      }, reply => {
        if (cancelled) return;
        const approved = service.approve(owner, reply, startedAt);
        setNegotiation(projectionNegotiationState(reply, approved));
        if (approved) {
          failures = 0;
          timer = browserDeliveryClock.delay(negotiate, PROJECTION_OBSERVER_POLICY.heartbeatMs);
        } else if (['busy_or_closed', 'retiring', 'not_authorized_or_joined', 'unavailable_or_invalid']
          .includes(reply?.reason) && failures++ < 2) {
          timer = browserDeliveryClock.delay(negotiate, PROJECTION_OBSERVER_POLICY.retryMs);
        }
      });
    };
    negotiate();
    return () => {
      cancelled = true;
      if (timer !== null) browserDeliveryClock.cancel(timer);
      service.cancel();
    };
  }, [enabled, ready, sequenceKnown, service, owner]);
  useEffect(() => () => service.cancel(), [service]);
  return useMemo(() => ({ ...service, negotiation }), [service, negotiation]);
}
